import { Link, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import { useState, useEffect, useMemo } from 'react'
import { useGlobalSchema } from '../hooks/useSchemaOrg'
import { schemaGraph, schemaOrganization, schemaWebSite } from '../lib/schema-org'
import { useQuery } from '@tanstack/react-query'
import { useHomeCrtChannel } from '../context/HomeCrtChannelContext'
import { channelNumForPath, buildCrtCrumbs } from '../lib/crt-breadcrumb'
import { fetchEvents, fetchBranding } from '../api/public'
import { fetchSiteChrome, FALLBACK_SITE_CHROME, type SiteChrome } from '../api/site-chrome'
import { ChromeLink } from '../components/ChromeLink'
import { pickPromotedUpcoming } from '../lib/pick-promoted-event'
import { useAuth } from '../context/AuthContext'

function useStripCountdown(targetIso: string | null): string {
  const [display, setDisplay] = useState('---:--:--')
  useEffect(() => {
    if (!targetIso) { setDisplay('---:--:--'); return }
    const target = new Date(targetIso).getTime()
    function tick() {
      const diff = Math.max(0, target - Date.now())
      const totalH = Math.floor(diff / 3600000)
      const mm = Math.floor((diff % 3600000) / 60000)
      const ss = Math.floor((diff % 60000) / 1000)
      setDisplay(`${String(totalH).padStart(3, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`)
    }
    tick()
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [targetIso])
  return display
}

function pathMatch(pathname: string, href: string): boolean {
  if (href === '/') return pathname === '/'
  return pathname === href || pathname.startsWith(`${href}/`)
}

export function Layout({ children }: { children: ReactNode }) {
  const loc = useLocation()
  const p = loc.pathname
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { meta } = useHomeCrtChannel()
  const { user } = useAuth()

  const ch = channelNumForPath(p, meta?.ch ?? null)
  const crumbs = buildCrtCrumbs(p, null)
  const sectionLabel = ((crumbs[1] ?? crumbs[0])?.label ?? 'BNKR').toUpperCase()
  const sectionPath = p === '/' ? '/' : '/' + (p.split('/').filter(Boolean)[0] ?? '')

  const brandingQ = useQuery({
    queryKey: ['branding'],
    queryFn: fetchBranding,
    staleTime: 10 * 60 * 1000,
  })
  const chromeQ = useQuery({
    queryKey: ['site-chrome'],
    queryFn: fetchSiteChrome,
    staleTime: 5 * 60 * 1000,
  })
  const chrome: SiteChrome = chromeQ.data?.site ?? FALLBACK_SITE_CHROME
  const logoUrl = brandingQ.data?.logoUrl || '/bnkr-logo.svg'

  const globalSchema = useMemo(
    () => schemaGraph(schemaOrganization({ logoUrl, chrome }), schemaWebSite()),
    [logoUrl, chrome.contactEmail, chrome.brandSub, chromeQ.dataUpdatedAt, chromeQ.isFetched],
  )
  useGlobalSchema(globalSchema)
  const marqueeParts = (chrome.marqueeText ?? 'BNKR · BUNKER EVENTS')
    .split('·')
    .map((s) => s.trim())
    .filter(Boolean)
  const marqueeLine = marqueeParts.length
    ? marqueeParts.map((p) => (
        <span key={p}>
          {p} <span className="dot" />
        </span>
      ))
    : (
        <span>
          BNKR <span className="dot" />
        </span>
      )
  const eventsQ = useQuery({
    queryKey: ['events', 'upcoming'],
    queryFn: () => fetchEvents({ status: 'upcoming' }),
    staleTime: 5 * 60 * 1000,
  })
  const nextEvent = useMemo(() => {
    const ev = pickPromotedUpcoming(eventsQ.data?.events)
    if (!ev || new Date(ev.startsAt).getTime() <= Date.now()) return null
    return ev
  }, [eventsQ.data?.events])
  const countdownDisplay = useStripCountdown(nextEvent?.startsAt ?? null)
  const countdownTo = nextEvent ? `/events/${nextEvent.slug}` : '/events'

  const mainClass =
    p === '/' || p === '/events'
      ? 'main--theme-shell'
      : /^\/events\/.+/.test(p)
        ? 'main--theme-event'
        : 'main-padding'

  return (
    <>
      <header className="site-header">
        <div className="wrap">
          <div className="row">
            <Link to="/" className="brand" onClick={() => setDrawerOpen(false)}>
              <span className="brand-row">
                <img src={logoUrl} alt="" aria-hidden className="brand-logo" />
                <span className="brand-text">
                  <span>[<span className="crt">BNKR</span>]</span>
                  <span className="brand-sub">{chrome.brandSub ?? 'Bunker-Events'}</span>
                </span>
              </span>
            </Link>
            <nav className="nav" aria-label="Hauptnavigation">
              {chrome.navLinks.map((l) => (
                <ChromeLink
                  key={`${l.href}-${l.label}`}
                  item={l}
                  className={pathMatch(loc.pathname, l.href) ? 'active' : ''}
                  onClick={() => setDrawerOpen(false)}
                />
              ))}
              {chrome.shopLink ? (
                <ChromeLink
                  item={chrome.shopLink}
                  onClick={() => setDrawerOpen(false)}
                />
              ) : null}
              <Link
                to={user ? '/member' : '/login'}
                className={`nav-member-link${p.startsWith('/member') || p === '/login' ? ' active' : ''}`}
                onClick={() => setDrawerOpen(false)}
              >
                {user ? '⬡ Member' : 'Login'}
              </Link>
            </nav>
            <button
              type="button"
              className="menu-btn"
              aria-expanded={drawerOpen}
              aria-controls="site-nav-drawer"
              aria-label="Menü"
              onClick={() => setDrawerOpen((o) => !o)}
            >
              Menu
            </button>
          </div>
        </div>
      </header>

      <div className="site-transmission-strip" aria-hidden={false}>
        <div className="wrap">
          <div className="meta-row">
            <span className="ch">
              <span style={{ color: 'var(--crt)' }}>CH {ch}</span>
              <span style={{ color: 'var(--ink-3)' }}>/</span>
              <Link to={sectionPath} style={{ color: 'inherit', textDecoration: 'none' }}><b>{sectionLabel}</b></Link>
            </span>
            <Link to={countdownTo} style={{ color: 'inherit', textDecoration: 'none', fontFamily: 'var(--mono)', letterSpacing: '.12em', fontSize: 12 }}>
              {countdownDisplay}
            </Link>
          </div>
        </div>
      </div>

      <div
        id="site-nav-drawer"
        className={`site-crt-drawer ${drawerOpen ? 'is-open' : ''}`}
        aria-hidden={!drawerOpen}
      >
        <div className="site-crt-drawer__panel">
          {chrome.navLinks.map((l) => (
            <ChromeLink
              key={`drawer-${l.href}-${l.label}`}
              item={l}
              className={pathMatch(loc.pathname, l.href) ? 'active' : ''}
              onClick={() => setDrawerOpen(false)}
            />
          ))}
          {chrome.shopLink ? (
            <ChromeLink item={chrome.shopLink} onClick={() => setDrawerOpen(false)} />
          ) : null}
          <Link
            to={user ? '/member' : '/login'}
            className={p.startsWith('/member') || p === '/login' ? 'active' : ''}
            onClick={() => setDrawerOpen(false)}
          >
            {user ? '⬡ Member Area' : 'Login / Member'}
          </Link>
        </div>
      </div>

      <main className={mainClass}>{children}</main>

      <div className="marquee" aria-hidden>
        <div className="marquee-track">
          <span>{marqueeLine}</span>
          <span>{marqueeLine}</span>
        </div>
      </div>

      <footer className="site-footer" id="footer">
        <div className="wrap">
          <div className="footer-grid">
            <div className="footer-brand">
              <div className="display" style={{ fontSize: 'clamp(56px, 9vw, 128px)', lineHeight: 0.86 }}>
                [BNKR]
              </div>
              <div className="kicker" style={{ marginTop: 14, letterSpacing: '0.22em' }}>
                {chrome.brandSub ?? 'Bunker-Events'}
              </div>
              {chrome.contactEmail ? (
                <p style={{ marginTop: 18, color: 'var(--ink-2)' }}>
                  <a href={`mailto:${chrome.contactEmail}`}>{chrome.contactEmail}</a>
                </p>
              ) : null}
            </div>
            {chrome.footerColumns.map((col) => (
              <div key={col.title} className="footer-col">
                <h5>{col.title}</h5>
                <ul>
                  {col.links.map((link) => (
                    <li key={`${col.title}-${link.href}-${link.label}`}>
                      <ChromeLink item={link} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="footer-end">
            <span>{chrome.copyright ?? '© BNKR Bunker Verein'}</span>
            <span style={{ display: 'flex', gap: 24 }}>
              <Link to="/impressum">Impressum</Link>
              <Link to="/datenschutz">Datenschutz</Link>
            </span>
          </div>
        </div>
      </footer>
    </>
  )
}
